import {Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {ModalityCodeEnum} from 'domain/usecases';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {textHandle} from 'presentation/utils/textHandle';
import {formatRefundAmount, formatRefundDate} from 'presentation/pages/private/refund/utils';

import {GridContainer, IconWrapper} from './styles';
import type {RefundDetailsTabGridProps} from './types';

const {modalityLabel, protocol} = translations['pt-br'].commons;
const {
	amountToReceive,
	detailValueNotInformed,
	expectedPaymentDateTitle,
	fiscalNoteLabel,
	originProtocol,
	providerLabel,
	providerNamePlaceholder,
	requestedAt,
	modality: refundModalityLabels,
} = translations['pt-br'].privateFlow.consultRefund;
const {cards: modalityCards} = translations['pt-br'].privateFlow.refund.serviceType;

const MODALITY_LABEL_BY_CODE: Record<ModalityCodeEnum, string> = {
	[ModalityCodeEnum.MEDICAL_APPOINTMENT]: modalityCards.consult,
	[ModalityCodeEnum.SIMPLE_EXAMS]: refundModalityLabels.simpleExam,
	[ModalityCodeEnum.SPECIAL_EXAMS]: refundModalityLabels.specialExam,
	[ModalityCodeEnum.HOSPITALIZATIONS]: modalityCards.hospitalization,
	[ModalityCodeEnum.EMERGENCY]: modalityCards.emergencyRoom,
	[ModalityCodeEnum.THERAPIES]: modalityCards.therapy,
};

export function RefundDetailsTabGrid({refund}: RefundDetailsTabGridProps) {
	const theme = useTheme();
	const fallback = detailValueNotInformed;

	const modalityValue =
		MODALITY_LABEL_BY_CODE[String(refund?.modalityCode) as ModalityCodeEnum] ?? fallback;
	const providerValue = refund?.provider?.trim()
		? textHandle(refund.provider).capitalize()
		: providerNamePlaceholder;

	const requestSourceValue = refund?.requestSource?.trim();

	const items = [
		{
			icon: 'text-box-outline',
			label: modalityLabel,
			value: modalityValue,
		},
		{
			icon: 'person-outline',
			label: providerLabel,
			value: providerValue,
		},
		{
			icon: 'barcode-default',
			label: fiscalNoteLabel,
			value: refund?.fiscalNumber?.trim() ? refund.fiscalNumber : fallback,
		},
		{
			icon: 'calendar-clock',
			label: expectedPaymentDateTitle,
			value: refund?.estimatedPaymentDate?.trim()
				? formatRefundDate(refund.estimatedPaymentDate)
				: fallback,
		},
		{
			icon: 'calendar-focused-filled',
			label: requestedAt,
			value: refund?.requestDate?.trim() ? formatRefundDate(refund.requestDate) : fallback,
		},
		...(requestSourceValue
			? ([
					{
						icon: 'tag-filled',
						label: originProtocol,
						value: requestSourceValue,
					},
				] as const)
			: ([] as const)),
		{
			icon: 'assignment-outline',
			label: protocol,
			value: refund?.protocol?.trim() ? refund.protocol : fallback,
		},
		{
			icon: 'paid',
			label: amountToReceive,
			value: formatRefundAmount(refund?.situationCode, refund?.totalReimbursementAmount),
		},
	] as const;

	return (
		<GridContainer>
			{items.map(({icon, label, value}) => (
				<Stack key={label} direction="row" gap={1.5} alignItems="flex-start">
					<IconWrapper>
						<Icon name={icon} size={24} color={theme.palette.primary.main} />
					</IconWrapper>
					<Stack gap={0.5} minWidth={0}>
						<Typography variant="body2" fontWeight={600} color="text.secondary">
							{label}
						</Typography>
						<Typography
							variant="body1"
							color="text.primary"
							sx={{wordBreak: 'break-word'}}>
							{value}
						</Typography>
					</Stack>
				</Stack>
			))}
		</GridContainer>
	);
}
